import React from "react";
import Table from "react-bootstrap/Table";

import { CartStateProps } from "./cart-types";
import {
  cartToProductName,
  cartToTotalPrice,
  cartToUnitPrice
} from "./cartSlice";
import { CART_CURRENCY, type CartState } from "./cartSlice";
import type { CartNew } from "./piosolverStoreApi";

function cartStateToProduct(cart: CartNew, state: CartState) {
  if (state.orderType === "offer") {
    return {
      title: "Offer",
      titleShort: "Offer",
      price: state.calculatedPrice?.amount ?? 0,
      priceActivation: undefined
    };
  }
  if (state.orderType === "cloud-credits") {
    return {
      title: "Cloud Credits",
      titleShort: "Cloud Credits",
      price: state.calculatedPrice?.amount ?? 0,
      priceActivation: undefined
    };
  }
  const options = cart.availableProducts;
  return options.filter((o) => o.titleShort === state.version)[0];
}

function ContactSummary({ cart, state }: CartBodyProps) {
  const step = state.status.step;
  if (step < 1) return null;
  if (step > 3) return null;
  return (
    <div className="mb-4">
      <Table size="sm">
        <tbody>
          <tr>
            <th scope="row">Email</th>
            <td>{state.contactEmail ?? ""}</td>
          </tr>
          <tr>
            <th scope="row">Name</th>
            <td>
              {state.contactName?.givenName ?? ""}{" "}
              {state.contactName?.familyName ?? ""}
            </td>
          </tr>
          <tr>
            <th scope="row">Address</th>
            <td>
              <div>{state.billingAddress?.streetAddress ?? ""}</div>
              <div>
                {state.billingAddress?.city ?? ""}{" "}
                {state.billingAddress?.state ?? ""}{" "}
                {state.billingAddress?.postalCode ?? ""}
              </div>
              <div>{state.billingAddress?.countryName ?? ""}</div>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

type DiscountProps = {
  discount: number;
  isUpgrade: boolean;
  numberOfLicenses: number;
};
function Discount({ discount, isUpgrade }: DiscountProps) {
  if (discount < 1) return null;
  // Only show the discount information on an upgrade
  if (!isUpgrade) return null;
  // const message = isUpgrade
  //   ? "Discount for license trade-in"
  //   : "Discount for multiple activations";
  const message = "Discount for license trade-in";
  return (
    <tr className="text-danger">
      <td>{message}</td>
      <td className="text-end">
        {CART_CURRENCY}
        {(discount * -1).toFixed(2)}
        {/* this is information about discount in base price, no need to add info about taxes */}
      </td>
    </tr>
  );
}

type PricingMessageProps = {
  message: string | undefined;
};
function PricingMessage({ message }: PricingMessageProps) {
  if (message == null) return null;
  return (
    <div className="text-black-50" style={{ fontSize: "smaller" }}>
      {message}
    </div>
  );
}

function UpgradeMessage({ message }: PricingMessageProps) {
  if (message == null) return null;
  return (
    <div className="text-danger" style={{ fontSize: "smaller" }}>
      {message}
    </div>
  );
}

type TotalDisplayProps = {
  totalPrice: string;
};
function TotalDisplay({ totalPrice }: TotalDisplayProps) {
  return (
    <div className="d-flex justify-content-between">
      <div className="fw-bold">Total</div>
      <div className="fw-bold">
        {CART_CURRENCY}
        {totalPrice}
        {" "}(+ applicable taxes)
        {/* this is total amount displayed to the user */}
      </div>
    </div >
  );
}

function computeFullUnitPrice(
  productPrice: number,
  priceActivation: number | undefined,
  numberOfLicenses: number,
  unitPrice: number | undefined
) {
  if (productPrice <= 0) return unitPrice ?? 0;
  const numberOfAdditionalLicenses = Math.max(0, numberOfLicenses - 1);
  const totalPrice =
    productPrice +
    (priceActivation ?? productPrice) * numberOfAdditionalLicenses;
  return totalPrice;
}

type CartBodyProps = CartStateProps & { cart: CartNew };
function OrderSummary({ cart, state }: CartBodyProps) {
  const step = state.status.step;
  if (step < 1) return null;
  if (step > 3) return null;
  const error = state.calculatedPrice?.error;
  if (error != null) return null;
  const product = cartStateToProduct(cart, state);
  const isUpgrade = state.orderType === "upgrade";
  const totalPrice = cartToTotalPrice(state);
  const unitPrice = cartToUnitPrice(state);
  const productName = cartToProductName(state) ?? product.title;
  const supportedUntil = state.calculatedPrice?.supportedUntil;
  const message = state.calculatedPrice?.message;
  const numberOfLicenses = state.numberOfLicenses ?? 1;
  const fullUnitPrice = computeFullUnitPrice(
    product.price,
    product.priceActivation,
    numberOfLicenses,
    unitPrice
  );
  const discount =
    unitPrice != null && unitPrice < fullUnitPrice
      ? fullUnitPrice - unitPrice
      : 0;

  if (state.orderType === "cloud-credits" || state.orderType === "offer")
    return (
      <OrderSummaryTableNonLicense
        productName={productName}
        totalPrice={totalPrice}
      />
    );

  return (
    <OrderSummaryTable
      discount={discount}
      isUpgrade={isUpgrade}
      fullUnitPrice={fullUnitPrice}
      message={message}
      numberOfLicenses={state.numberOfLicenses ?? 1}
      productName={productName}
      supportedUntil={supportedUntil}
      totalPrice={totalPrice}
      upgradeMessage={state.calculatedPrice?.upgradeMessage}
    />
  );
}

type OrderSummaryTableProps = {
  discount: number;
  isUpgrade: boolean;
  fullUnitPrice: number;
  message?: string;
  numberOfLicenses: number;
  productName: string;
  supportedUntil?: string;
  totalPrice: string;
  upgradeMessage?: string;
};
function OrderSummaryTable({
  discount,
  fullUnitPrice,
  isUpgrade,
  message,
  numberOfLicenses,
  productName,
  supportedUntil,
  totalPrice,
  upgradeMessage
}: OrderSummaryTableProps) {
  if (discount == 0)
    return (
      <div className="mb-4 order-summary">
        <Table size="sm">
          <thead>
            <tr>
              <th scope="col">Product</th>
              <th scope="col" className="text-end">
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {productName},{" "}
                {numberOfLicenses == 1
                  ? "one computer"
                  : `up to ${numberOfLicenses} computers`}{" "}
                {supportedUntil && (
                  <span style={{ fontSize: "smaller" }}>
                    <br />
                    Updates until {supportedUntil}
                  </span>
                )}
              </td>
              <td className="fw-bold text-end">
                {CART_CURRENCY}
                {totalPrice}
                {" "}(+ applicable taxes)
                {/* this is displayed at the main cart page near to product selection */}
              </td>
            </tr>
          </tbody>
        </Table>
        <PricingMessage message={message} />
        <UpgradeMessage message={upgradeMessage} />
      </div>
    );
  return (
    <div className="mb-4 order-summary">
      <Table size="sm">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col" className="text-end">
              Price
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {productName}{" "}
              {supportedUntil && (
                <span style={{ fontSize: "smaller" }}>
                  <br />
                  Updates until {supportedUntil}
                </span>
              )}
            </td>
            <td className="text-end">
              {CART_CURRENCY}
              {fullUnitPrice.toFixed(2)}
              {/* this is partial calculation - no need to add info about applicable taxes here */}
            </td>
          </tr>
          <Discount
            discount={discount}
            isUpgrade={isUpgrade}
            numberOfLicenses={numberOfLicenses}
          />
        </tbody>
      </Table>
      <TotalDisplay totalPrice={totalPrice} />
      <PricingMessage message={message} />
      <UpgradeMessage message={upgradeMessage} />
    </div>
  );
}

function OrderSummaryTableNonLicense({
  productName,
  totalPrice
}: Pick<OrderSummaryTableProps, "productName" | "totalPrice">) {
  return (
    <div className="mb-4 order-summary">
      <Table size="sm">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col" className="text-end">
              Price
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{productName}</td>
            <td className="fw-bold text-end">
              {CART_CURRENCY}
              {totalPrice}
              {" "}(+ applicable taxes)
              {/* this is displayed for example when purchasing an offer */}
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

export default OrderSummary;
export { ContactSummary, OrderSummaryTable };
